import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DECLARED_CERTIFICATE_OTHER_CODE,
  DECLARED_CERTIFICATE_OPTIONS,
  declaredCertificateTestSlug,
  INDUSTRY_SECTOR_OPTIONS,
  INDUSTRY_SECTOR_OTHER_CODE,
} from "@/constants/company-form-options";
import { useLegalFormOptions } from "@/features/onboarding/hooks/useLegalFormOptions";
import type { CompanyProfileFormData } from "./profile";
interface SettingsCompanySectionProps {
  profileData: CompanyProfileFormData;
  profileStatus: string | null;
  profileError: string | null;
  profileLoading: boolean;
  profileSaving: boolean;
  onProfileChange: (
    field: keyof CompanyProfileFormData,
    value: string | string[],
  ) => void;
  onIndustryToggle: (value: string) => void;
  onCertificateToggle: (value: string) => void;
  onProfileSave: (event: React.FormEvent) => void | Promise<void>;
}

export default function SettingsCompanySection({
  profileData,
  profileStatus,
  profileError,
  profileLoading,
  profileSaving,
  onProfileChange,
  onIndustryToggle,
  onCertificateToggle,
  onProfileSave,
}: SettingsCompanySectionProps) {
  const { t } = useTranslation(["settings", "validation", "onboarding"]);
  const { options: legalFormOptions } = useLegalFormOptions();
  const selectClassName =
    "mt-2 w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] shadow-sm focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]";

  return (
    <section className="space-y-4 rounded-lg border border-[var(--border)] bg-[var(--card)] p-6">
      <div>
        <h2 className="text-lg font-semibold text-[var(--foreground)]">
          {t("settings:company.title")}
        </h2>
        <p className="text-sm text-[var(--muted-foreground)]">{t("settings:company.subtitle")}</p>
      </div>

      {profileLoading
        ? <div className="text-sm text-[var(--muted-foreground)]">{t("settings:loading.profile")}</div>
        : (
          <form onSubmit={onProfileSave} className="space-y-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="company-name">{t("settings:company.companyName")}</Label>
              <Input
                className="mt-2"
                id="company-name"
                onChange={(event) => onProfileChange("company_name", event.target.value)}
                required
                type="text"
                value={profileData.company_name}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--foreground)]">
                {t("settings:company.legalForm")}
              </label>
              <select
                value={profileData.legal_form}
                onChange={(event) => {
                  onProfileChange("legal_form", event.target.value);
                  if (event.target.value !== "other") onProfileChange("legal_form_other", "");
                }}
                className={selectClassName}
                required
              >
                {legalFormOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {profileData.legal_form === "other" && (
                <Input
                  className="mt-2"
                  onChange={(event) => onProfileChange("legal_form_other", event.target.value)}
                  placeholder={t("settings:company.legalFormOtherPlaceholder")}
                  type="text"
                  value={profileData.legal_form_other ?? ""}
                />
              )}
            </div>

            <div>
              <p className="block text-sm font-medium text-[var(--foreground)]">
                {t("settings:company.industrySector")}
              </p>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                {t("settings:company.industrySectorHint")}
              </p>
              <div className="mt-2 max-h-52 overflow-y-auto rounded-[var(--r-lg)] border border-[var(--input)] bg-[var(--background)] p-3">
                <div className="space-y-2">
                  {INDUSTRY_SECTOR_OPTIONS.map((option) => (
                    <label key={option.value} className="flex items-start gap-2 text-sm text-[var(--foreground)]">
                      <input
                        type="checkbox"
                        checked={profileData.industry_sector.includes(option.value)}
                        onChange={() => onIndustryToggle(option.value)}
                        className="mt-0.5 rounded border-[var(--input)] text-[var(--primary)] focus:ring-[var(--ring)]"
                        data-test={`settings-industry-${option.value.toLowerCase()}`}
                      />
                      <span>{t(option.labelKey)}</span>
                    </label>
                  ))}
                </div>
              </div>
              {profileData.industry_sector.includes(INDUSTRY_SECTOR_OTHER_CODE) && (
                <Input
                  className="mt-2"
                  onChange={(event) => onProfileChange("industry_sector_other", event.target.value)}
                  placeholder={t("settings:company.industrySectorOtherPlaceholder")}
                  type="text"
                  value={profileData.industry_sector_other}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--foreground)]">
                {t("settings:company.nip")}
              </label>
              <Input
                className="mt-2"
                id="company-nip"
                inputMode="numeric"
                onChange={(event) => onProfileChange("nip", event.target.value)}
                placeholder={t("settings:company.nipPlaceholder")}
                required
                type="text"
                value={profileData.nip}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--foreground)]">
                {t("settings:company.businessActivity")}
              </label>
              <textarea
                value={profileData.business_activity}
                onChange={(event) => onProfileChange("business_activity", event.target.value)}
                className="mt-2 w-full min-h-[120px] rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] shadow-sm placeholder:text-[var(--muted-foreground)] focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]"
                placeholder={t("settings:company.businessActivityPlaceholder")}
                required
              />
            </div>

            {/* pirx-lus53: optional declared-certificate multi-select + 'other' free-text */}
            <div>
              <p className="block text-sm font-medium text-[var(--foreground)]">
                {t("settings:company.declaredCertificate.label")}
              </p>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                {t("settings:company.declaredCertificate.hint")}
              </p>
              <div className="mt-2 max-h-52 overflow-y-auto rounded-[var(--r-lg)] border border-[var(--input)] bg-[var(--background)] p-3">
                <div className="space-y-2">
                  {DECLARED_CERTIFICATE_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-start gap-2 text-sm text-[var(--foreground)]"
                    >
                      <input
                        type="checkbox"
                        checked={profileData.declared_certificate.includes(option.value)}
                        onChange={() => onCertificateToggle(option.value)}
                        className="mt-0.5 rounded border-[var(--input)] text-[var(--primary)] focus:ring-[var(--ring)]"
                        data-test={`settings-declared-certificate-${declaredCertificateTestSlug(option.value)}`}
                      />
                      <span>{t(option.labelKey)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                {t("settings:company.declaredCertificate.disclaimer")}
              </p>
              {profileData.declared_certificate.includes(DECLARED_CERTIFICATE_OTHER_CODE) && (
                <Input
                  className="mt-2"
                  data-test="settings-declared-certificate-other-text"
                  onChange={(event) => onProfileChange("declared_certificate_other", event.target.value)}
                  placeholder={t(
                    "settings:company.declaredCertificate.otherPlaceholder",
                  )}
                  type="text"
                  value={profileData.declared_certificate_other}
                />
              )}
            </div>
            {profileStatus && (
              <Alert variant="success">
                <AlertDescription>{profileStatus}</AlertDescription>
              </Alert>
            )}
            {profileError && (
              <Alert variant="error">
                <AlertDescription>{profileError}</AlertDescription>
              </Alert>
            )}
            <div className="flex justify-end">
              <Button
                variant="cta"
                type="submit"
                disabled={profileSaving}
                data-test="settings-company-save"
              >
                {profileSaving
                  ? t("settings:company.updating")
                  : t("settings:company.updateCompany")}
              </Button>
            </div>
          </form>
        )}
    </section>
  );
}
